import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User, AuthTokens, LoginCredentials } from '@/types'
import { authService } from '@services/authService'
import { setUserId, trackLogin } from '../utils/analytics'

export type PerspectiveType = 'admin' | 'student' | 'client' | 'professor'

export interface AuthCompany {
  id: string
  nome: string
  apelido?: string
  documento?: string
  [key: string]: unknown
}

interface AuthState {
  user: User | null
  originalUser?: User | null
  tokens: AuthTokens | null
  originalTokens?: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  activePerspective: PerspectiveType
  activeCompanyId: string | null
  activeCompany: AuthCompany | null
  allowedCompanies: AuthCompany[]
  simulatedClientPermissions: string[] | null
  simulatedClientUserName: string | null
  // Actions
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
  revertImpersonation: () => Promise<void>
  setUser: (user: User) => void
  setAccessToken: (accessToken: string) => void
  refreshTokens: () => Promise<void>
  setPerspective: (perspective: PerspectiveType) => void
  setActiveCompanyId: (companyId: string | null) => void
  setActiveCompany: (company: AuthCompany | null) => void
  setAllowedCompanies: (companies: AuthCompany[]) => void
  setSimulatedClientPermissions: (perms: string[] | null, name?: string | null) => void
}

interface NavigatorStandalone extends Navigator {
  standalone?: boolean
}

interface PlaywrightWindow extends Window {
  __PLAYWRIGHT__?: boolean
}

function shouldUseLocalStorage(): boolean {
  const isPWA =
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as NavigatorStandalone).standalone)
  const isPlaywright =
    Boolean((window as PlaywrightWindow).__PLAYWRIGHT__) ||
    Boolean(localStorage.getItem('isPlaywright'))
  return isPWA || isPlaywright
}

const pwaAwareStorage = {
  getItem: (name: string) => {
    return shouldUseLocalStorage() ? localStorage.getItem(name) : sessionStorage.getItem(name)
  },
  setItem: (name: string, value: string) => {
    if (shouldUseLocalStorage()) {
      localStorage.setItem(name, value)
      sessionStorage.removeItem(name)
    } else {
      sessionStorage.setItem(name, value)
      localStorage.removeItem(name)
    }
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name)
    sessionStorage.removeItem(name)
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      originalUser: null,
      tokens: null,
      originalTokens: null,
      isAuthenticated: false,
      isLoading: false,
      activePerspective: 'admin',
      activeCompanyId: null,
      activeCompany: null,
      allowedCompanies: [],
      simulatedClientPermissions: null,
      simulatedClientUserName: null,

      login: async (credentials) => {
        set({ isLoading: true })
        try {
          const { user, tokens } = await authService.login(credentials)
          
          let perspective: PerspectiveType = 'admin'
          if (user.role === 'student' || user.role === 'aluno') {
            perspective = 'student'
          } else if (user.role === 'client' || user.role === 'cliente') {
            perspective = 'client'
          } else if (user.role === 'professor' || user.role === 'instrutor') {
            perspective = 'professor'
          }
          
          set({ 
            user, 
            tokens, 
            isAuthenticated: true,
            activePerspective: perspective,
            activeCompanyId: null,
            activeCompany: null,
            simulatedClientPermissions: null,
            simulatedClientUserName: null
          })

          // GA4: Define o ID de usuário para rastreamento cross-device e dispara o evento de login
          if (user?.id) {
            setUserId(user.id)
            trackLogin('email_password', user.role)
          }

          // Inicia a solicitação/inscrição de Push Notifications Web
          void import('@services/push.service')
            .then(m => m.pushService.subscribeToPush())
            .catch(e => console.error('Push error', e))
        } catch (error) {
          set({ user: null, tokens: null, isAuthenticated: false })
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      logout: () => {
        // Resetamos todo o estado de autenticação
        set({ 
          user: null, 
          tokens: null, 
          originalUser: null,
          originalTokens: null,
          isAuthenticated: false,
          activePerspective: 'admin',
          activeCompanyId: null,
          activeCompany: null,
          allowedCompanies: [],
          simulatedClientPermissions: null,
          simulatedClientUserName: null
        })
        useAuthStore.persist.clearStorage()
      },

      revertImpersonation: async () => {
        const backupUser = get().originalUser
        const backupTokens = get().originalTokens
        try {
          const { api } = await import('@services/api')
          const res = await api.post('/auth/impersonate/revert')
          const data = res.data.data
          localStorage.removeItem('tenantSlug')
          sessionStorage.removeItem('tenantSlug')
          sessionStorage.removeItem('gsp_imp_handshake')
          set({
            user: data.user,
            tokens: data.tokens,
            originalUser: null,
            originalTokens: null,
            isAuthenticated: true,
            activePerspective: 'admin',
            activeCompanyId: null,
            activeCompany: null,
            allowedCompanies: [],
            simulatedClientPermissions: null,
            simulatedClientUserName: null
          })
          window.location.href = '/companies/tenants'
        } catch (err) {
          // Fallback se a API falhar ou a sessão de 30min tiver expirado: usa o backup local
          console.warn('Revert impersonation API fallback acionado:', err)
          const originalUser = get().originalUser || backupUser
          const originalTokens = get().originalTokens || backupTokens
          if (originalUser && originalTokens) {
            localStorage.removeItem('tenantSlug')
            sessionStorage.removeItem('tenantSlug')
            sessionStorage.removeItem('gsp_imp_handshake')
            set({
              user: originalUser,
              tokens: originalTokens,
              originalUser: null,
              originalTokens: null,
              isAuthenticated: true,
              activePerspective: 'admin',
              activeCompanyId: null,
              activeCompany: null,
              allowedCompanies: [],
              simulatedClientPermissions: null,
              simulatedClientUserName: null
            })
            window.location.href = '/companies/tenants'
          } else {
            get().logout()
          }
        }
      },

      setUser: (user) => set({ user }),

      setAccessToken: (accessToken: string) => {
        const tokens = get().tokens
        if (tokens) {
          set({ tokens: { ...tokens, accessToken } })
        }
      },

      refreshTokens: async () => {
        const { tokens } = get()
        if (!tokens?.refreshToken) {
          throw new Error('No refresh token available')
        }

        try {
          const newTokens = await authService.refresh(tokens.refreshToken)
          set({ tokens: newTokens })
        } catch (error) {
          get().logout()
          throw error
        }
      },

      setPerspective: (perspective) => {
        if (perspective === 'admin') {
          set({ activePerspective: perspective, simulatedClientPermissions: null, simulatedClientUserName: null })
        } else {
          set({ activePerspective: perspective })
        }
      },
      setActiveCompanyId: (companyId) => set({ activeCompanyId: companyId }),
      setActiveCompany: (company) => set({ activeCompany: company }),
      setAllowedCompanies: (companies) => set({ allowedCompanies: companies }),
      setSimulatedClientPermissions: (perms, name = null) => set({ simulatedClientPermissions: perms, simulatedClientUserName: name })
    }),
    {
      name: 'gsp-auth',
      storage: createJSONStorage(() => pwaAwareStorage),
      partialize: (state) => ({ 
        user: state.user, 
        originalUser: state.originalUser,
        tokens: state.tokens, 
        originalTokens: state.originalTokens,
        isAuthenticated: state.isAuthenticated, 
        activePerspective: state.activePerspective,
        activeCompanyId: state.activeCompanyId,
        activeCompany: state.activeCompany,
        allowedCompanies: state.allowedCompanies,
        simulatedClientPermissions: state.simulatedClientPermissions,
        simulatedClientUserName: state.simulatedClientUserName
      })
    }
  )
)
